<template>
  <div id="deactivate_employee_modal" class="modal custom-modal fade" role="dialog">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Deactivate Employee - {{ employeeName }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref="closeBtn">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="deactivateEmployee" class="clearfix">
            <div class="input-block mb-3">
              <label class="col-form-label font-weight-medium">Deactivation Date <span class="text-danger">*</span></label>
              <div class="cal-icon">
                <input
                  type="date"
                  class="form-control date-input"
                  v-model="deactivationDate"
                  required
                  @click="openDatePicker"
                />
              </div>
            </div>
            
            <div class="alert alert-info border-0 rounded-3 p-3 mb-4">
              <p class="mb-2" style="font-size: 13px; line-height: 1.5; color: #084298;">
                <i class="fa-solid fa-circle-info me-1"></i>
                If you select <strong>today's date or a past date</strong>, the user will be deactivated immediately.
              </p>
              <p class="mb-0" style="font-size: 13px; line-height: 1.5; color: #084298;">
                <i class="fa-solid fa-clock me-1"></i>
                If you select a <strong>future date</strong>, the deactivation will be scheduled, and the user will remain active until that date.
              </p>
            </div>

            <div class="submit-section text-center mt-3">
              <button class="btn btn-danger submit-btn text-white px-4 py-2" type="submit" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                Confirm Deactivation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { VUE_APP_API_BASE_URL } from "../../config";

export default {
  name: "DeactivateEmployeeModal",
  data() {
    return {
      userId: null,
      employeeName: "",
      deactivationDate: "",
      loading: false,
    };
  },
  methods: {
    getLocalToday() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatToYYYYMMDD(dateVal) {
      if (!dateVal) return "";
      
      if (typeof dateVal === "string") {
        const trimmed = dateVal.trim();
        // YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          return trimmed.substring(0, 10);
        }
        // DD-MM-YYYY format
        if (/^\d{2}-\d{2}-\d{4}/.test(trimmed)) {
          const parts = trimmed.substring(0, 10).split("-");
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }

      return "";
    },
    updateValues(employee) {
      this.userId = employee.id || employee.user_id;
      this.employeeName = employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
      
      const todayStr = this.getLocalToday();
      
      let parsedDate = this.formatToYYYYMMDD(employee.scheduled_deactivation_date);
      if (!parsedDate) {
        parsedDate = todayStr;
      }
      this.deactivationDate = parsedDate;
    },
    openDatePicker(event) {
      if (event.target && typeof event.target.showPicker === 'function') {
        try {
          event.target.showPicker();
        } catch (e) {
          // ignore if picker is already active or unsupported
        }
      }
    },
    async deactivateEmployee() {
      if (!this.deactivationDate) {
        return;
      }

      this.loading = true;
      try {
        const response = await axios.post(`${VUE_APP_API_BASE_URL}/user/${this.userId}/deactivate`, {
          deactivation_date: this.deactivationDate,
        });

        if (response.data.status) {
          this.$toast.success(response.data.message || "Action processed successfully");
          this.$emit("deactivated");
          this.$refs.closeBtn.click();
        } else {
          this.$toast.error(response.data.message || "Failed to process deactivation");
        }
      } catch (error) {
        console.error(error);
        const errorMsg = error.response?.data?.message || error.response?.data?.errors || "Something went wrong";
        this.$toast.error(typeof errorMsg === 'object' ? Object.values(errorMsg).flat().join(', ') : errorMsg);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.input-block {
  float: left;
  width: 100%;
  margin-bottom: 1rem;
}

.date-input {
  cursor: pointer;
  background-color: #fff !important;
  border: 1px solid #ced4da !important;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  width: 100%;
  display: block;
}

.date-input:focus {
  border-color: #0d6efd !important;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15) !important;
}

.alert-info {
  clear: both;
  float: left;
  width: 100%;
  background-color: #e8f4ff !important;
  border: 1px solid #b6e0fe !important;
  color: #084298 !important;
  box-sizing: border-box;
}

.alert-info p {
  color: #084298 !important;
  word-break: normal;
  overflow-wrap: break-word;
}

.alert-info i {
  color: #0d6efd !important;
}

.submit-section {
  clear: both;
  float: left;
  width: 100%;
  text-align: center;
  margin-top: 20px;
}

.submit-btn {
  color: #ffffff !important;
  background-color: #e63946 !important;
  border-color: #e63946 !important;
  font-weight: 600;
  border-radius: 50px;
  min-width: 200px;
  transition: all 0.2s ease-in-out;
}

.submit-btn:hover:not(:disabled) {
  background-color: #d62839 !important;
  border-color: #d62839 !important;
  box-shadow: 0 4px 10px rgba(230, 57, 70, 0.3);
}

.submit-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}
</style>
